import { get } from 'svelte/store';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { format } from 'svelte-i18n';
//Helpers
import { convertToUnit } from '../../units_helpers';
import { getIcon } from '../../emeters_data_helpers';
import { is_gen_2, load_image_from, real_id } from '../../interface_helpers';
import { devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { getTimeDuration } from './ev_charger_session_info_helpers';
//Types
import type { VirtualComponentInterfaceData, VCInterfaceStatus } from 'src/types/VirtualComponents';
import type { Device } from 'src/types/Device';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
import type { SubtitlePropsTypes } from '@Shelly/shelly-design-system/index';

export const updateEvChargerStatus = (
	interface_data: VCInterfaceStatus[],
	data: VCInterfaceStatus,
	valueData: VirtualComponentInterfaceData,
): void => {
	const { role } = data;
	if (/^(work_state)$/.test(role)) {
		const workState = getEvWorkingState(data.event);
		data.value = data.event === 'charger_charging' ? workState.subtitle : workState.title;
		data.icon = workState.icon;
		data.iconColor = workState.iconColor;
	} else if (/^(auto_start|start_charging)$/.test(role)) {
		data.iconColor = valueData.value ? '--color-success' : null;
		data.advLabel = true;
	} else if (/^(time_charge)$/.test(role)) {
		data.value = getTimeDuration(valueData.value);
	} else if (/^(phase_info)$/.test(role)) {
		data.hideLabel = true;
		data.value = valueData.value;
		['a', 'b', 'c', 'total_current', 'total_power', 'total_act_energy'].forEach((p) => {
			const phase = valueData?.value?.[`phase_${p}`];
			if (phase) {
				Object.keys(phase).forEach((i) => {
					const unit = getUnit(i);
					if (/^(power|current|voltage)$/.test(i)) {
						interface_data.push({
							...data,
							name: `${p}_${i}`,
							vcName: `${p}_${i}`,
							label: i,
							icon: getIcon(i),
							unit,
							value: convertToUnit(phase[i] ?? 'N/A', unit),
							rawValue: phase[i],
							hideLabel: false,
							advLabel: true,
						});
					}
				});
			} else if (valueData?.value?.[p] != null) {
				const unit = getUnit(p);
				interface_data.push({
					...data,
					value: convertToUnit(valueData.value[p], unit),
					name: p,
					vcName: p,
					role: p,
					unit,
					icon: getIcon(p),
					hideLabel: false,
				});
			}
		});
		return;
	}
	interface_data.push(data);
};

const getUnit = (type: string) => {
	let unit = 'kW';
	switch (type) {
		case 'voltage':
			unit = 'V';
			break;
		case 'total_act_energy':
			unit = 'kWh';
			break;
		case 'total_power':
		case 'power':
			unit = 'kW';
			break;
		case 'current':
		case 'total_current':
			unit = 'A';
			break;
	}
	return unit;
};

export type EVWorkingStates =
	| 'charger_free'
	| 'charger_insert'
	| 'charger_free_fault'
	| 'charger_wait'
	| 'charger_charging'
	| 'charger_pause'
	| 'charger_end'
	| 'charger_fault';
export type StatusIndicatorWorkingStates =
	| 'idle'
	| 'waiting'
	| 'paused'
	| 'charging'
	| 'completed'
	| 'error';

export const getEvWorkingState = (
	evChargerState: EVWorkingStates,
): {
	state: StatusIndicatorWorkingStates;
	title: string;
	subtitle: string;
	icon: string;
	iconColor: string;
} => {
	const $format = get(format);
	let state = null;
	let title = null;
	let icon = null;
	let iconColor = null;
	let subtitle = $format('label_current_state', { default: 'Current state' });
	switch (evChargerState) {
		// EVSE is powered and ready; **car plug not inserted**. No active session;
		// user may start a new charge when a vehicle is connected.
		case 'charger_free':
			state = 'idle';
			title = $format('label_idle', { default: 'Idle' });
			icon = 'fas fa-plug';
			iconColor = '--color-muted';
			break;
		// Session is active but ** power delivery is halted ** (e.g., user paused, waiting for schedule /
		// solar surplus, Auto Balance throttled to zero). Resumable without unplugging.
		case 'charger_pause':
		// Plug inserted into the vehicle but not yet handshaked/authorized. Typically a transient state before WAIT;
		//  currently **no explicit UI label** (may only show a connector icon).
		case 'charger_insert':
			state = 'paused';
			title = $format('label_paused', { default: 'Paused' });
			icon = 'fas fa-pause';
			iconColor = '--color-success';
			break;
		// Unable to unlock from the vehicle’s side(vehicle latch not releasing).
		// Edge case likely shown as an ** error ** with guidance to unlock from the car or stop / retry the session.
		case 'charger_free_fault':
		// Fault condition detected (e.g., over-temp, RCD trip, ground fault).
		// Show error code and recommended action; charging disabled until issue is cleared.
		case 'charger_fault':
			state = 'error';
			title = $format('alert_danger_error_heading', { default: 'Error' });
			icon = 'fas fa-xmark';
			iconColor = '--color-danger';
			break;
		// Plug inserted and **handshake successful**. Charger is **waiting to start** due to schedule window,
		// authorization, energy mode (e.g., Full-Green waiting for surplus), or Auto Balance limit.
		case 'charger_wait':
			state = 'waiting';
			title = $format('label_waiting', { default: 'Waiting' });
			icon = 'fas fa-hourglass-half';
			iconColor = '--color-muted';
			break;
		// Charging session ongoing; power is being delivered. Show real-time current (A), power (kW),
		// and session energy (kWh).
		case 'charger_charging':
			state = 'charging';
			subtitle = $format('label_charging', { default: 'Charging' });
			icon = 'fas fa-battery-bolt';
			iconColor = '--color-success';
			break;
		// Charging session ended normally (limit reached, user stopped, or car signaled full).
		// Display session summary (kWh, duration). Safe to unplug.
		case 'charger_end':
			state = 'completed';
			title = $format('label_completed', { default: 'Completed' });
			icon = 'fas fa-check';
			iconColor = '--color-success';
			break;
	}
	if (!state) return null;
	return {
		state,
		title,
		subtitle,
		icon,
		iconColor,
	};
};

type EMDevice = {
	id?: string;
	title: string;
	deviceIP: string;
	subtitle: SubtitlePropsTypes;
	avatar: { image?: string; icon?: string };
};

export const handleDeviceByIP = (device: Device): EMDevice => {
	const status = readDeviceStatus(device.id);
	const deviceIP = status?.eth?.ip || status?.wifi?.sta_ip || '';
	return {
		id: device.id,
		title: device.name,
		deviceIP,
		subtitle: {
			value: `${device.ssid && !/\?\?\?/.test(device.ssid) ? device.ssid : ''} ${deviceIP}`,
			bold: false,
			textSmall: true,
		},
		avatar: { image: load_image_from(device.image, device?.server) },
	};
};

export const findDeviceByIP = (deviceIP: string): EMDevice => {
	const allDevices = get(devices);
	const allDevicesStatuses = get(devices_status);
	const $format = get(format);
	const settings: any = Object.values(allDevicesStatuses).find((status: DeviceStatusG2) => {
		return (
			deviceIP &&
			(status?.['em:0'] || status?.['em1:0']) &&
			is_gen_2(allDevices[status.id]) &&
			!allDevices[status?.id]?.shared &&
			(status?.eth?.ip == deviceIP || status?.wifi?.sta_ip == deviceIP)
		);
	});
	const device: Device = allDevices[settings?.id];
	if (device) return handleDeviceByIP(device);
	return {
		title: deviceIP
			? $format('label_em_device_ip', {
					default: 'EM Device IP Address',
				})
			: $format('label_select_device', {
					default: 'Select device',
				}),
		deviceIP,
		subtitle: {
			value: deviceIP || '',
			bold: false,
			textSmall: true,
		},
		avatar: { icon: deviceIP ? 'fas fa-microchip' : 'fas fa-plus' },
	};
};
